import { flatten as flattenTree, prune as pruneTree } from '@/utils/tree.js'
import { decodeDate } from '@/utils/dates.js'

function getConceptName(cubeMeta, id) {
  return cubeMeta.conceptSchemes[0].concepts.find(concept => concept.id === id).names
}

function groupOptions(options) {
  const optionsMap = {}
  let groupedOptions = options.reduce((res, opt) => {
    let parent
    if (opt.parentCode) {
      parent = optionsMap[opt.parentCode]
      if (parent) {
        res.hasGroups = true
        parent.children = parent.children || []
        parent.children.push(opt)
      } else {
        res.push(opt)
      }
    } else {
      res.push(opt)
    }
    optionsMap[opt.id] = opt
    return res
  }, [])

  return groupedOptions
}

export function getAllDimensions(cubeMeta) {
  const dims = cubeMeta.dataStructures[0].dataStructureComponents.dimensionList.dimensions

  return dims.map(d => {
    const dim = {
      codelist: d.codelist,
      codelistVersion: d.codelistVersion,
      name: getConceptName(cubeMeta, d.id),
      id: d.id,
      options: groupOptions(getCodeListValues(cubeMeta, d.id, d.codelist, d.codelistVersion)),
      omissible: d.omissibleInUrl,
      type: d.type,
    }
    dim.notApplicable = flattenTree(dim.options).length <= 1
    return dim
  })
}

export function getIndicatorDimension(cubeMeta) {
  let allDims = getAllDimensions(cubeMeta)
  return allDims.find(d => d.type === 'indicator')
}

export function getNonIndicatorDimensions(cubeMeta) {
  let allDims = getAllDimensions(cubeMeta)
  return allDims.filter(d => d.type !== 'indicator')
}

export function getCountryDimension(cubeMeta) {
  const allDims = getAllDimensions(cubeMeta)
  const dim = allDims.find(d => d.type === 'country')
  dim.options = flattenTree(dim.options)
  return dim
}

export function getUnitDimension(cubeMeta) {
  const allDims = getAllDimensions(cubeMeta)
  return allDims.find(d => d.type === 'unit')
}

export function getTimeScaleDimension(cubeMeta) {
  const allDims = getAllDimensions(cubeMeta)
  return allDims.find(d => d.type === 'frequency')
}

export function getBreakdownDimensions(cubeMeta) {
  let allDims = getAllDimensions(cubeMeta)
  return allDims.filter(d => /breakdown/.test(d.type))
}

export function getTimeDimension(cubeMeta) {
  const dim = cubeMeta.dataStructures[0].dataStructureComponents.dimensionList.timeDimensions[0]
  return {
    codelist: null,
    name: getConceptName(cubeMeta, dim.id),
    id: dim.id,
    options: [],
  }
}

export function getCodeListValues(cubeMeta, dimId, id, version) {
  const codelist = cubeMeta.codelists.find(list => list.id === id && list.version === version)

  if (!codelist) return []

  let isTivaFamily = [
    'DSD_TIVA_MAINLV@DF_MAINLV',
    'DSD_TIVA_MAINSH@DF_MAINSH',
    'DSD_TIM_2023@DF_TIM_2023',
  ].includes(getCubeId(cubeMeta))

  return codelist.codes[dimId].map(option => {
    const parentCode = option.parent || null
    return {
      id: option.id,
      parentCode,
      label: null,
      labels: Object.keys(option.names).reduce((labels, key) => {
        if (isTivaFamily && dimId === 'ACTIVITY' && id === 'CL_ACTIVITY_ISIC4') {
          // Format labels on TIVA industries options
          labels[key] = option.id + ': ' + option.names[key]
        } else {
          labels[key] = option.names[key]
        }
        return labels
      }, {}),
      isDefault: option.isDefault,
    }
  })
}

export function dataToTable(data) {
  const colNameDict = {
    'Observation Status': 'Flags',
  }

  const countryDim = data.structures[0].dimensions.observation.find(d => d.type === 'country')
  const countryDimIndex =
    countryDim !== void 0
      ? countryDim.keyPosition ||
        data.structures[0].dimensions.observation.findIndex(d => d.id === countryDim.id)
      : null

  let headers = data.structures[0].dimensions.observation
    .map(d => d.name)
    .concat([
      'Country ISO3',
      'Value',
      ...data.structures[0].attributes.observation.map(at => colNameDict[at.name] || at.name),
    ])
  headers.types = data.structures[0].dimensions.observation
    .map(d => d.type)
    .concat(['iso', ...data.structures[0].attributes.observation.map(() => 'attribute')])
  let attributes = data.structures[0].attributes.observation
  let rows = Object.keys(data.dataSets[0].observations).map(rowId => {
    let iso3
    let dimValues = rowId.split(':').map((valueIdx, idx) => {
      if (idx === countryDimIndex)
        iso3 = data.structures[0].dimensions.observation[idx].values[valueIdx].id
      return data.structures[0].dimensions.observation[idx].values[valueIdx].name
    })
    let attrValues = data.dataSets[0].observations[rowId]
    attrValues = attrValues.map((a, i) => {
      if (i === 0) return a
      else if (a === null) return a
      else
        return attributes[i - 1].values
          ? attributes[i - 1].values[a]?.name || attributes[i - 1].values[a]?.value
          : a
    })
    return [...dimValues, iso3, ...attrValues].map(d => (d === null ? '' : d))
  })

  return [headers, ...rows]
}

export function dataToChartTable(data) {
  const standardData = standarizeDataSet(data.dataSets[0], data.structures[0])
  const headers = ['_COUNTRY_', '_DATE_', '_VALUE_', '_ISO3_', '_UNIT_']
  const rows = standardData.map(datum => {
    const row = [datum.country, datum.date, datum.value, datum.iso3, datum.unit]
    row.brkDims = datum.brkDims
    row.attributes = datum.attributes
    return row
  })

  return [headers, ...rows]
}

export function standarizeDataSet(dataSet, structure) {
  if (!(dataSet.observations && Object.keys(dataSet.observations).length)) return []

  const dimensions = structure.dimensions.observation
  const order = dimensions.reduce((acum, dim, idx) => {
    if (/breakdown/.test(dim.type)) {
      acum.breakdowns = acum.breakdowns || []
      acum.breakdowns.push(idx)
      return acum
    }
    acum[dim.type] = idx
    return acum
  }, {})

  const attrFields = structure.attributes.observation
  const observations = dataSet.observations
  const standardData = Object.keys(observations).map(rowId => {
    const datumIdx = rowId.split(':')
    const datum = datumIdx.map((index, position) => {
      return dimensions[position].values[index].id
    }, {})

    const observation = [...observations[rowId]]
    const value = observation.shift()
    const attributes = observation.reduce((acum, index, position) => {
      const field = attrFields[position]
      acum[field.id] = field.values[index]?.id || field.values[index]?.value
      return acum
    }, {})

    const unit = order.unit ? dimensions[order.unit].values[datumIdx[order.unit]].id : null
    return {
      value,
      iso3: dimensions[order.country].values[datumIdx[order.country]].id,
      country: dimensions[order.country].values[datumIdx[order.country]].name,
      date: decodeDate(datum[order.time], datum[order.frequency]),
      unit,
      brkDims: order.breakdowns.reduce((acum, position) => {
        const dimId = dimensions[position].id
        acum[dimId] = datum[position]
        return acum
      }, {}),
      attributes,
    }
  })

  return standardData
}

export function getCubeTimeRange(cubeMeta) {
  const timeDimension = getTimeDimension(cubeMeta)
  return timeDimension.options.reduce(
    (range, option) => {
      range[0] = Math.min(range[0], parseInt(option.label, 10) || range[0])
      range[1] = Math.max(range[1], parseInt(option.label, 10) || range[1])
      return range
    },
    [Infinity, -Infinity]
  )
}

export function getAvailableDates(data) {
  try {
    return data.structures[0].dimensions.observation
      .find(dim => dim.type === 'time')
      .values.map(d => decodeDate(d.id))
  } catch (err) {
    return []
  }
}

export function getAvailableCountries(data) {
  try {
    return data.structures[0].dimensions.observation
      .find(dim => dim.type === 'country')
      .values.map(d => d.id)
  } catch (err) {
    return []
  }
}

export function getAgencyId(cubeMeta) {
  return cubeMeta.conceptSchemes[0].agencyID
}

export function getCubeId(cubeMeta) {
  return cubeMeta.dataflows[0].id
}

export function getCubeName(cubeMeta) {
  return cubeMeta.dataflows[0].name
}

export function getCubeVersion(cubeMeta) {
  return cubeMeta.dataflows[0].version
}

export function getIndicatorName(cubeMeta, indId, lng) {
  const dim = getIndicatorDimension(cubeMeta)
  let name = getCodeListValues(cubeMeta, dim.id, dim.codelist, dim.codelistVersion).find(
    d => d.id === indId
  ).labels[lng]
  const cubeId = cubeMeta.dataflows[0].id
  return sharesNameDecorator(name, cubeId)
}

export function applyCubeConstraints(cubeMeta) {
  const constraint = cubeMeta.contentConstraints.find(c => c.type === 'Actual')
  if (!constraint) return cubeMeta

  const cubeDimensions = cubeMeta.dataStructures[0].dataStructureComponents.dimensionList.dimensions
  const codelists = cubeMeta.codelists
  constraint.cubeRegions[0].keyValues.forEach(dimConstraint => {
    const dim = cubeDimensions.find(dim => dim.id === dimConstraint.id)
    if (!dim) return

    const dimConfig = cubeMeta.config.dimensions.find(dim => dim.conceptRef === dimConstraint.id)
    const codelist = codelists.find(
      list => dim.codelist === list.id && dim.codelistVersion === list.version
    )

    const defaultValue = dimConfig.defaultValue || '_T'
    const availableOptions = Object.fromEntries(dimConstraint.values.map(v => [v, true]))
    codelist.codes[dim.id] = codelist.codes
      .filter(option => availableOptions[option.id])
      .map(option => ({ ...option, isDefault: option.id === defaultValue }))
  })

  return cubeMeta
}

export function extendCubeWithConfig(cubeMeta, { config, flip }) {
  cubeMeta.config = config
  const dimensions = cubeMeta.dataStructures[0].dataStructureComponents.dimensionList.dimensions
  dimensions.forEach(dim => {
    const codeListId = dim.localRepresentation.enumeration.match(/(?:Codelist=[^:]+:)(.*)+/)[1]
    const [codeList, version] = codeListId.split('(')
    dim.codelist = codeList
    dim.codelistVersion = version.slice(0, version.length - 1)
    const dimConfig = config.dimensions.find(dimConfig => dimConfig.conceptRef === dim.id)
    dim.type = dimConfig.type
    dim.omissibleInUrl = ['unit', 'frequency'].indexOf(dimConfig.type) > -1
  })

  if (flip) {
    rewriteCubeDimensionsTypes(cubeMeta, { [flip]: 'country', country: flip })
  }

  return cubeMeta
}

export function extendDataWithConfig(data, { meta, config, flip }) {
  const dimsMeta = meta.dataStructures[0].dataStructureComponents.dimensionList.dimensions.concat(
    meta.dataStructures[0].dataStructureComponents.dimensionList.timeDimensions[0]
  )

  if (!data.dataSets[0].observations) {
    return data
  }

  data.structures[0].dimensions.observation.forEach(dim => {
    const dimMeta = dimsMeta.find(meta => meta.id === dim.id)
    const dimConfig = config.dimensions.find(d => d.conceptRef === dimMeta.id)
    if (dimConfig) {
      dim.metaId = dimConfig.metaId
      dim.type = dimConfig.type
      dim.omissible = dimConfig.omissibleInUrl
    } else {
      dim.type = 'time'
    }
  })

  if (flip) {
    rewriteDataDimensionsTypes(data, { [flip]: 'country', country: flip })
  }

  return data
}

export function extendDataWithMeta(data, meta) {
  function setLngs(dim, meta) {
    const options = flattenTree(meta.options).reduce((acum, opt) => {
      acum[opt.id] = opt.labels
      return acum
    }, {})
    dim.values.forEach(d => (d.labels = options[d.id]))
  }

  const cubeDimensions = getAllDimensions(meta)

  if (!data.structures[0].dimensions.observation) {
    return data
  }

  data.structures[0].dimensions.observation.forEach(dim => {
    const dimensionMeta = cubeDimensions.find(d => d.id === dim.id) || getTimeDimension(meta)
    setLngs(dim, dimensionMeta)
  })

  return data
}

export function pruneBreakdowns(breakdownDimensions, filter, selection) {
  if (!filter || breakdownDimensions.length === 0) {
    return breakdownDimensions
  }

  let level = 1
  let dim = breakdownDimensions.find(dim => dim.type === 'breakdown' + level)
  do {
    const hasGroups = dim.options.hasGroups
    const filterIds = Object.fromEntries(filter.breakdowns.map(d => [d.metaId, true]))
    const selectedId = selection[dim.id] || filter.breakdowns[0].metaId

    let options = pruneTree(dim.options, filterIds).filter(opt => filterIds[opt.id])
    options.hasGroups = hasGroups
    dim.options = options
    filter = filter.breakdowns.find(d => d.metaId === selectedId)
    level++
    dim = breakdownDimensions.find(dim => dim.type === 'breakdown' + level)
    // if (dim && dim.id === 'CL_GD_BREAKDOWNS_21_POLICY') {
    //   level++
    //   dim = breakdownDimensions.find(dim => dim.type === 'breakdown' + level)
    // }
  } while (dim)

  return breakdownDimensions
}

export function rewriteCubeDimensionsTypes(cubeMeta, changesMap) {
  cubeMeta.dataStructures[0].dataStructureComponents.dimensionList.dimensions.forEach(dim => {
    dim.type = changesMap[dim.type] || dim.type
  })
  return cubeMeta
}

export function rewriteDataDimensionsTypes(data, changesMap) {
  data.structures[0].dimensions.observation.forEach(
    dim => (dim.type = changesMap[dim.type] || dim.type)
  )
  return data
}

export function getTimeScales(data) {
  const scales = data.structures[0].dimensions.observation.find(d => d.id === 'FREQ')?.values || []
  return scales.map(d => {
    switch (d.id) {
      case 'P1D':
      case 'D':
        d.labels = {
          en: 'Daily',
          fr: 'Quotidien',
        }
        break
      case 'P1M':
      case 'M':
        d.labels = {
          en: 'Monthly',
          fr: 'Mensuel',
        }
        break
      case 'P3M':
      case 'Q':
        d.labels = {
          en: 'Quarterly',
          fr: 'Trimestrielle',
        }
        break
      case 'P6M':
      case 'S':
        d.labels = {
          en: 'Semianual',
          fr: 'Semestriel',
        }
        break
      case 'P1Y':
      case 'A':
        d.labels = {
          en: 'Annual',
          fr: 'Annuel',
        }
        break
      default:
        d.labels = {
          en: d.name,
          fr: d.name,
        }
    }

    return d
  })
}

export function getUnits(data) {
  const units =
    data.structures[0].dimensions.observation.find(d => d.id === 'UNIT_MEASURE')?.values || []
  return units
}

export function sharesNameDecorator(indName, cubeId) {
  if (/DF_MAINSH$/i.test(cubeId) && !/(shares|ratio|contributions)/i.test(indName)) {
    return indName.replace(/, shares$/, '') + ', shares'
  }

  return indName
}
